import { useEffect } from 'react';
import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';

// Input in the stream is PAIRS of messages to UE — KeyDown/KeyUp, MouseDown/MouseUp — and the engine holds
// the input until the second half arrives. The pixel-streaming library listens for the release on `document`
// (keys) and on the video element or `window` (mouse), so a release that happens while this page does not have
// focus is never seen: hold W, alt-tab (or Cmd-Tab, or click another window), let go there, and the KeyDown is
// the last thing UE ever heard — the avatar keeps walking after the user comes back, and pressing W again
// changes nothing, because as far as the browser is concerned the key has long been up. Alt/Cmd sticks the
// same way, being held at the moment focus is taken; a held mouse button sticks the same way and leaves the
// camera dragging.
//
// The library knows the failure — MouseControllerLocked releases the held keys when the pointer lock drops,
// with this exact comment — but only in locked-mouse mode, and the hub runs `HoveringMouse: true`. There is no
// blur/visibility handling anywhere else in 1.3.0, and UE has none either, so the page closes the pairs itself.
// GameCast already does this for its own client (GameCast/hooks.tsx); this is the MYTA_STREAMS half.

// UE's JavaScriptKeyCodeToFKey table splits the right-hand modifiers out of the legacy keyCodes the browser
// reports and normalizes the Cmd/Win keys the browsers disagree about. Mirrored from the library's private
// KeyboardController.getKeycode: a KeyUp carrying a different number than the KeyDown did releases the wrong
// key and leaves the real one held.
const RIGHT_SHIFT = 253;
const RIGHT_CONTROL = 254;
const RIGHT_ALT = 255;
const META_LEFT = 91;
const META_RIGHT = 92;
// An IME composition keystroke, which the library never sends a KeyDown for — so it is never held either.
const IME_COMPOSITION = 229;

export const ueKeyCode = (event: KeyboardEvent): number | null => {
  switch (event.code) {
    case 'ShiftRight':
      return RIGHT_SHIFT;
    case 'ControlRight':
      return RIGHT_CONTROL;
    case 'AltRight':
      return RIGHT_ALT;
    case 'MetaLeft':
    case 'OSLeft':
      return META_LEFT;
    case 'MetaRight':
    case 'OSRight':
      return META_RIGHT;
    default:
      break;
  }
  const code = event.keyCode;
  return !code || code === IME_COMPOSITION ? null : code;
};

export interface HeldKeys {
  down: (event: KeyboardEvent) => void;
  up: (event: KeyboardEvent) => void;
  // Returns what is still held AND forgets it in one step: the release path must never be able to send the
  // same KeyUp twice (blur and visibilitychange fire together on a tab switch), and a key whose KeyUp we have
  // already sent is not held any more by definition.
  drain: () => number[];
}

export const createHeldKeys = (): HeldKeys => {
  const held = new Set<number>();
  return {
    down: (event: KeyboardEvent) => {
      const code = ueKeyCode(event);
      if (code !== null) {
        held.add(code);
      }
    },
    up: (event: KeyboardEvent) => {
      const code = ueKeyCode(event);
      if (code !== null) {
        held.delete(code);
      }
    },
    drain: () => {
      const codes = [...held];
      held.clear();
      return codes;
    },
  };
};

export interface ButtonRelease {
  button: number;
  clientX: number;
  clientY: number;
}

export interface HeldButtons {
  down: (event: MouseEvent) => void;
  up: (event: MouseEvent) => void;
  move: (event: MouseEvent) => void;
  drain: () => ButtonRelease[];
}

// The position matters as much as the button: UE takes the coordinates off the MouseUp itself, so releasing at
// a stale point puts the click somewhere the user never was. The last position the pointer was seen at is the
// one the library's own MouseMove stream has already put UE's cursor on.
export const createHeldButtons = (): HeldButtons => {
  const held = new Set<number>();
  let clientX = 0;
  let clientY = 0;
  const track = (event: MouseEvent) => {
    clientX = event.clientX;
    clientY = event.clientY;
  };
  return {
    down: (event: MouseEvent) => {
      track(event);
      held.add(event.button);
    },
    up: (event: MouseEvent) => {
      track(event);
      held.delete(event.button);
    },
    move: track,
    drain: () => {
      const releases = [...held].map((button) => ({
        button,
        clientX,
        clientY,
      }));
      held.clear();
      return releases;
    },
  };
};

export const useReleaseHeldInput = (pixelStreaming: PixelStreaming | null) => {
  useEffect(() => {
    if (!pixelStreaming) {
      return;
    }

    const element = pixelStreaming.videoElementParent;
    const keys = createHeldKeys();
    const buttons = createHeldButtons();

    // Keys are watched exactly where the library watches them (`document`, bubble phase) rather than on the
    // window capture phase, so this set is the set UE believes is down: a hub field that swallows a keystroke
    // with stopPropagation hides it from both listeners, and one that does not is seen by both.
    const onKeyDown = (event: KeyboardEvent) => keys.down(event);
    const onKeyUp = (event: KeyboardEvent) => keys.up(event);

    // A press counts only if it landed on the video — that is the only kind the library forwards to UE. The
    // release is watched on the window, because the library moves its own mouseup listener there for the
    // duration of a press and a drag that ends off the element is normal.
    const onMouseDown = (event: MouseEvent) => buttons.down(event);
    const onMouseUp = (event: MouseEvent) => buttons.up(event);
    const onMouseMove = (event: MouseEvent) => buttons.move(event);

    const releaseHeld = () => {
      const codes = keys.drain();
      const releases = buttons.drain();
      if (codes.length) {
        try {
          const sendKeyUp = pixelStreaming.toStreamerHandlers.get('KeyUp');
          for (const code of codes) {
            sendKeyUp?.([code]);
          }
        } catch (e) {
          // A peer torn down between the blur and this send. The keys are forgotten either way — the stream
          // that held them is gone.
          console.warn('Failed to release the keys held at blur', e);
        }
      }
      // The button goes back through the library's own mouseup handler rather than straight to the streamer:
      // it owns the video-relative coordinate translation, and it also has to see the release to stop
      // capturing the window. Dispatched on `window` — where its listener sits while a button is down — and
      // deliberately non-bubbling, so nothing below the window mistakes this for a real click.
      for (const { button, clientX, clientY } of releases) {
        window.dispatchEvent(
          new MouseEvent('mouseup', {
            button,
            buttons: 0,
            clientX,
            clientY,
            bubbles: false,
            cancelable: true,
          }),
        );
      }
    };

    // Three ways the input stops being ours, none implying the others: `blur` is focus leaving (another
    // window, the address bar, devtools, an iframe on this page), `visibilitychange` is the tab being switched
    // away from or the window minimised, `pagehide` is a navigation or a bfcache eviction. Whichever fires
    // first drains both sets; the ones that follow find them empty.
    const onVisibility = () => {
      if (document.hidden) {
        releaseHeld();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    element.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('blur', releaseHeld);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', releaseHeld);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      element.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('mousemove', onMouseMove, true);
      window.removeEventListener('blur', releaseHeld);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', releaseHeld);
    };
  }, [pixelStreaming]);
};
